import { ActionService } from 'dashboard-services/services'

const EMPTY_ARRAY = []

export default (() => {
  const ON_CHANGE = 'ON_CHANGE_REVIEW_DL'
  const onChange = ActionService.makeActionCreator(ON_CHANGE, 'value', 'name')

  const RESET = 'RESET_REVIEW_DL'
  const reset = ActionService.makeActionCreator(RESET)
  const incrementCommentGeometryVersion = () => (dispatch, getState) => {
    const geometryVersion = getReviewState(getState()).commentGeometryVersion
    dispatch(onChange(geometryVersion + 1, 'commentGeometryVersion'))
  }

  const getReviewState = state => state.datalakeState.reviewState

  const toggleInList = ({ list, key }) => list.includes(key) ? list.filter(item => item !== key) : [...list, key]

  const areAllIncluded = ({ list, keys }) => keys.length > 0 && keys.every(key => list.includes(key))

  const toggleValue = name => () => (dispatch, getState) => dispatch(onChange(!getReviewState(getState())[name], name))

  const toggleKey = name => key => (dispatch, getState) => dispatch(onChange(toggleInList({ list: getReviewState(getState())[name], key }), name))

  // When every section already has the flag it is cleared for all of them, otherwise it is set for all of them.
  const toggleAll = name => sectionKeys => (dispatch, getState) =>
    dispatch(onChange(areAllIncluded({ list: getReviewState(getState())[name], keys: sectionKeys }) ? EMPTY_ARRAY : sectionKeys, name))

  const expandSection = key => (dispatch, getState) => {
    const collapsedSections = getReviewState(getState()).collapsedSections
    if(collapsedSections.includes(key)) {
      dispatch(onChange(collapsedSections.filter(item => item !== key), 'collapsedSections'))
    }
  }

  const changeComments = update => (dispatch, getState) => {
    const comments = getReviewState(getState()).comments
    return dispatch(onChange({ ...comments, ...update(comments) }, 'comments'))
  }

  const updateCommentTemporary = update => changeComments(({ temporary }) => ({ temporary: update(temporary) }))

  const getLocationKey = ({ section, line }) => `${section}:${line}`

  const getMinimizedLocations = ({ activeLocation, minimized }) =>
    activeLocation ? [...new Set([...minimized, getLocationKey(activeLocation)])] : minimized

  const withNewTemporary = ({ temporary, location: { section, line }, value }) => ({
    ...temporary,
    new: {
      ...temporary.new,
      [section]: { ...temporary.new?.[section], [line]: value }
    }
  })

  const withoutTemporary = ({ temporary, index, location }) => {
    const remainingTemporary = { ...temporary }
    if(index != null) {
      const commentTemporary = { ...temporary[index] }
      delete commentTemporary.edit
      if(Object.keys(commentTemporary).length) {
        remainingTemporary[index] = commentTemporary
      } else {
        delete remainingTemporary[index]
      }
    } else {
      const { section, line } = location,
            newTemporary = { ...temporary.new },
            sectionTemporary = { ...newTemporary[section] }
      delete sectionTemporary[line]
      if(Object.keys(sectionTemporary).length) {
        newTemporary[section] = sectionTemporary
      } else {
        delete newTemporary[section]
      }
      if(Object.keys(newTemporary).length) {
        remainingTemporary.new = newTemporary
      } else {
        delete remainingTemporary.new
      }
    }
    return remainingTemporary
  }

  const getRetainedTemporary = ({ activeLocation, temporary, isWriting }) => {
    const draft = activeLocation && temporary.new?.[activeLocation.section]?.[activeLocation.line]
    if(draft && !isWriting && !draft.text.trim()) {
      return withoutTemporary({ temporary, location: activeLocation })
    }
    return temporary
  }

  const openComment = ({ location, index = null, anchor, create = true }) => changeComments(comments => {
    const locationKey = getLocationKey(location),
          previous = comments.activeLocation && getLocationKey(comments.activeLocation),
          temporary = previous !== locationKey || !create ? getRetainedTemporary(comments) : comments.temporary

    return {
      activeLocation: location,
      selectedIndex: index,
      minimized: getMinimizedLocations(comments).filter(item => item !== locationKey),
      temporary: create && !temporary.new?.[location.section]?.[location.line]
        ? withNewTemporary({ temporary, location, value: { text: '', anchor } })
        : temporary
    }
  })

  const minimizeComment = () => changeComments(comments => ({
    temporary: getRetainedTemporary(comments),
    minimized: getMinimizedLocations(comments),
    activeLocation: null
  }))

  const changeCommentTemporary = ({ index, location, text }) => updateCommentTemporary(temporary => index != null
    ? { ...temporary, [index]: { ...temporary[index], edit: { ...temporary[index]?.edit, text } } }
    : withNewTemporary({ temporary, location, value: { ...temporary.new?.[location.section]?.[location.line], text } }))

  const editCommentTemporary = ({ index, text }) => updateCommentTemporary(temporary => ({
    ...temporary,
    [index]: { ...temporary[index], edit: { text } }
  }))

  const cancelCommentTemporary = ({ index, location }) => updateCommentTemporary(temporary => withoutTemporary({ temporary, index, location }))

  const acquireCommentWrite = () => (dispatch, getState) => {
    if(getReviewState(getState()).comments.isWriting) {
      return false
    }
    dispatch(changeComments(() => ({ isWriting: true })))
    return true
  }

  const releaseCommentWrite = () => changeComments(() => ({ isWriting: false }))

  const removeCommentReference = ({ index }) => changeComments(comments => ({
    selectedIndex: comments.selectedIndex === index
      ? null
      : comments.selectedIndex > index ? comments.selectedIndex - 1 : comments.selectedIndex,
    preview: null,
    temporary: Object.fromEntries(Object.entries(comments.temporary)
      .filter(([key]) => key !== String(index))
      .map(([key, temporary]) => [key !== 'new' && Number(key) > index ? Number(key) - 1 : key, temporary]))
  }))

  const previewComment = ({ location, index }) => (dispatch, getState) => {
    dispatch(expandSection(location.section))
    const state = getReviewState(getState())
    if(!state.showAllSections.includes(location.section)) {
      dispatch(onChange([...state.showAllSections, location.section], 'showAllSections'))
    }
    dispatch(changeComments(comments => ({ preview: { location, index, sequence: (comments.preview?.sequence || 0) + 1 } })))
  }

  const toggleCommentPreview = ({ location, index }) => (dispatch, getState) => {
    const { activeLocation, selectedIndex } = getReviewState(getState()).comments
    if(activeLocation?.section === location.section && activeLocation?.line === location.line && (selectedIndex == null || selectedIndex === index)) {
      dispatch(minimizeComment())
      return
    }
    dispatch(previewComment({ location, index }))
  }

  return {
    openComment,
    minimizeComment,
    changeCommentTemporary,
    editCommentTemporary,
    cancelCommentTemporary,
    acquireCommentWrite,
    releaseCommentWrite,
    removeCommentReference,
    previewComment,
    toggleCommentPreview,
    ON_CHANGE,
    onChange,
    RESET,
    reset,
    incrementCommentGeometryVersion,
    areAllIncluded,
    toggleSidebar: toggleValue('isSidebarVisible'),
    toggleWrap: toggleValue('wrap'),
    toggleSectionCollapse: toggleKey('collapsedSections'),
    toggleSectionShowAll: toggleKey('showAllSections'),
    toggleAllCollapsed: toggleAll('collapsedSections'),
    toggleAllShown: toggleAll('showAllSections'),
    expandSection
  }
})()
